import { z } from "zod";
import { ACCESS_DEFAULTS_MAX } from "./access-codes";
import {
	AC_CARD_REMARKS_MAX,
	AC_CARD_STATUSES,
	AC_CARD_TRANSITIONS,
	AC_CARD_TRANSITION_ACTIONS,
	type AcCardStatus,
	type AcCardTransitionAction,
	BULK_ACCREDITATIONS_MAX_FILTER,
	BULK_ACCREDITATIONS_MAX_IDS,
	accreditationListQuery,
} from "./accreditation";
import { PERSON_GENDERS } from "./competition/participants";
import { paginationQuery } from "./pagination";
import { type TravelFormMessages, departureNotBeforeArrival, enTravelFormMessages, makeTravelLegFields } from "./travel";

/**
 * Request bodies of the accreditation WRITE endpoints (M3 S3): create, patch,
 * transitions, bulk, card access, delete and registration windows — plus the
 * small response shapes those endpoints answer with.
 *
 * Wire keys are snake_case. Every string trims BEFORE it checks (bug 5). The
 * register form uses these same factories with translated messages
 * (`apps/gms/src/lib/i18n/use-form-schemas.ts`); the API uses the English
 * defaults and only adds the camelCase mapping.
 *
 * ── What the register steps write, column by column ─────────────────────────
 *
 *   Zone        ac_cards: games_id, responsible_organization_id, main_function_id,
 *               sub_agency_id, sub_function_id
 *   Personal    people: country_code (nationality), identification_type,
 *               identification_number, written_name_in_identification,
 *               first_middle_name, last_surname, has_last_surname, gender,
 *               date_of_birth, place_of_birth (state / country of birth),
 *               identification_issuing_authority (issuing country),
 *               identification_issued_date, identification_date_of_expiry
 *   Additional  people: contact_name, phone_country_code, phone_number,
 *               email_address, country_of_residence, residential_address,
 *               emergency_contact_name, emergency_contact_relationship,
 *               emergency_phone_country_code, emergency_phone_number,
 *               blood_group, food_type, allergic, medical_condition,
 *               wheelchair_user, wheelchair_type
 *               ac_cards (media categories): employer_media_organization,
 *               original_job, employer_phone_country_code,
 *               employer_phone_number, employer_email_address, employer_address
 *   Sport       ac_cards: sport_id; people: height, weight, shirt_size,
 *               pants_size, shoe_size
 *   Card        ac_cards: accreditation_name (≤ 60), tv_short_name (≤ 20),
 *               delegation_role
 *   Review      ac_cards: urgent_printing; people: consent_truthfulness,
 *               consent_pdpa (the two declarations; `consented_at` is stamped
 *               by the server)
 *   Travel      travel_legs (M9): `arrival` / `departure`, one leg each per
 *               CARD (not per person), in the same transaction as the card.
 *               Optional at registration: the ticket may come later.
 */

// ── Limits ──────────────────────────────────────────────────────────────────

/** Name on card. */
export const ACCREDITATION_NAME_MAX = 60;
/** Name in results & on TV. */
export const TV_SHORT_NAME_MAX = 20;
/** Most free-text person / card fields. */
export const ACCREDITATION_TEXT_MAX = 200;
/** Addresses, medical conditions, allergies. */
export const ACCREDITATION_LONG_TEXT_MAX = 1000;
export const IDENTIFICATION_NUMBER_MAX = 40;
export const PHONE_MAX = 30;
export const EMAIL_MAX = 320;
/** The audit headline a caller may add to a write. */
export const ACCREDITATION_REASON_MAX = 300;

/** `people.identification_type` — the database enum `identification_type` (a unit test pins the two). */
export const IDENTIFICATION_TYPES = ["NATIONAL_ID", "PASSPORT", "OTHER"] as const;
export type IdentificationType = (typeof IDENTIFICATION_TYPES)[number];

/** Her meal-requirement chips, stored in `people.food_type text[]`. */
export const MEAL_REQUIREMENTS = ["HALAL", "VEGETARIAN", "VEGAN", "NO_BEEF", "NO_PORK", "GLUTEN_FREE", "LACTOSE_FREE", "NUT_ALLERGY"] as const;
export type MealRequirement = (typeof MEAL_REQUIREMENTS)[number];

export const WHEELCHAIR_TYPES = ["MANUAL", "MOTORISED"] as const;
export type WheelchairType = (typeof WHEELCHAIR_TYPES)[number];

/**
 * What `POST /accreditations/:id/submit` requires (Audrey's required fields
 * that have a column). Missing ones are answered 422 VALIDATION_FAILED with
 * `details.missing` listing these names, so the Review step can mark them.
 *
 *   sub_function_id   only when the category has sub-functions
 *   sport_id          only when the sub-function `requires_sport`
 *   photo             the card's accreditation photo (M4)
 *   id_front          the person's ID copy, front (M4) — temporarily optional
 *   id_back           its back — only for a NATIONAL_ID (MyKad) (M4) — temporarily optional
 *
 * To restore the ID copy: uncomment the two entries below and their conditions
 * in the API's `submitMissingSql` and the GMS's `missingForSubmit`.
 */
export const ACCREDITATION_SUBMIT_REQUIRED = [
	"identification_number",
	"first_middle_name",
	"gender",
	"phone_number",
	"email_address",
	"accreditation_name",
	"sub_function_id",
	"sport_id",
	"consent_truthfulness",
	"consent_pdpa",
	"photo",
	// The ID copy is temporarily optional (user, 2026-11-02): details may be typed by hand.
	// "id_front",
	// "id_back",
] as const;
export type AccreditationSubmitField = (typeof ACCREDITATION_SUBMIT_REQUIRED)[number];

// ── Messages (message-factory pattern) ──────────────────────────────────────

export interface AccreditationFormMessages {
	required: string;
	textTooLong: string;
	accreditationNameMax: string;
	tvShortNameMax: string;
	emailInvalid: string;
	dateInvalid: string;
	dateTimeInvalid: string;
	countryInvalid: string;
	numberInvalid: string;
	remarksRequired: string;
	personOrPeopleId: string;
	closesAfterOpens: string;
	nothingToChange: string;
	idsOrFilter: string;
}

export const enAccreditationFormMessages: AccreditationFormMessages = {
	required: "Required",
	textTooLong: "Too long",
	accreditationNameMax: `At most ${ACCREDITATION_NAME_MAX} characters`,
	tvShortNameMax: `At most ${TV_SHORT_NAME_MAX} characters`,
	emailInvalid: "Enter a valid email",
	dateInvalid: "Enter a valid date",
	dateTimeInvalid: "Enter a valid date and time",
	countryInvalid: "Pick a country",
	numberInvalid: "Enter a number",
	remarksRequired: "Enter a remark for the organisation",
	personOrPeopleId: "Enter the person's details, or pick an existing person",
	closesAfterOpens: "Must be after the opening time",
	nothingToChange: "Nothing to change",
	idsOrFilter: "Select records, or use the whole filtered list",
};

// ── Field builders ──────────────────────────────────────────────────────────

type M = AccreditationFormMessages;

const uuid = z.string().uuid();

/** Required text: trimmed, then 1..max. */
const requiredText = (m: M, max = ACCREDITATION_TEXT_MAX, maxMsg = m.textTooLong) => z.string().trim().min(1, m.required).max(max, maxMsg);

/**
 * Optional text: trimmed, then ≤ max; blank (or null) means "clear it" and
 * becomes null. Omitted leaves the stored value alone.
 */
const optionalText = (m: M, max = ACCREDITATION_TEXT_MAX, maxMsg = m.textTooLong) =>
	z
		.string()
		.trim()
		.max(max, maxMsg)
		.transform((v) => (v === "" ? null : v))
		.nullable()
		.optional();

const isRealDate = (v: string) => {
	const d = new Date(`${v}T00:00:00Z`);
	return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};
/** `YYYY-MM-DD`, a real calendar date. */
const dateField = (m: M) => z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, m.dateInvalid).refine(isRealDate, m.dateInvalid);
const optionalDate = (m: M) => dateField(m).nullable().optional();
/** ISO 3166 alpha-3 (`people.country_code` references `countries.iso_code`). */
const countryField = (m: M) => z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, m.countryInvalid);
const optionalEmail = (m: M) =>
	z
		.string()
		.trim()
		.max(EMAIL_MAX, m.textTooLong)
		.transform((v) => (v === "" ? null : v))
		.pipe(z.string().email(m.emailInvalid).nullable())
		.nullable()
		.optional();
/** An ID number must keep at least one letter or digit once normalised (the duplicate key ignores the rest). */
const idNumberField = (m: M) => requiredText(m, IDENTIFICATION_NUMBER_MAX).regex(/[A-Za-z0-9]/, m.required);
/** `numeric` columns (height, weight): a JSON number, stored exactly. */
const optionalMeasure = (m: M) => z.number({ invalid_type_error: m.numberInvalid }).min(0).max(999).nullable().optional();

// ── Person ──────────────────────────────────────────────────────────────────

/** Fields of `people` the register flow may write, all optional — PATCH's `person`. */
function personFields(m: M) {
	return {
		// Personal
		country_code: countryField(m).optional(),
		identification_type: z.enum(IDENTIFICATION_TYPES).optional(),
		identification_number: idNumberField(m).optional(),
		written_name_in_identification: optionalText(m),
		first_middle_name: requiredText(m).optional(),
		last_surname: optionalText(m),
		has_last_surname: z.boolean().optional(),
		gender: z.enum(PERSON_GENDERS).nullable().optional(),
		date_of_birth: dateField(m).optional(),
		place_of_birth: optionalText(m),
		identification_issuing_authority: optionalText(m),
		identification_issued_date: optionalDate(m),
		identification_date_of_expiry: optionalDate(m),
		// Additional
		contact_name: optionalText(m),
		phone_country_code: optionalText(m, 8),
		phone_number: optionalText(m, PHONE_MAX),
		email_address: optionalEmail(m),
		country_of_residence: countryField(m).nullable().optional(),
		residential_address: optionalText(m, ACCREDITATION_LONG_TEXT_MAX),
		emergency_contact_name: optionalText(m),
		emergency_contact_relationship: optionalText(m),
		emergency_phone_country_code: optionalText(m, 8),
		emergency_phone_number: optionalText(m, PHONE_MAX),
		blood_group: optionalText(m, 10),
		food_type: z.array(z.enum(MEAL_REQUIREMENTS)).max(MEAL_REQUIREMENTS.length).nullable().optional(),
		allergic: optionalText(m, ACCREDITATION_LONG_TEXT_MAX),
		medical_condition: optionalText(m, ACCREDITATION_LONG_TEXT_MAX),
		wheelchair_user: z.boolean().optional(),
		wheelchair_type: z.enum(WHEELCHAIR_TYPES).nullable().optional(),
		// Sport profile
		height: optionalMeasure(m),
		weight: optionalMeasure(m),
		shirt_size: optionalText(m, 10),
		pants_size: optionalText(m, 10),
		shoe_size: optionalText(m, 10),
		// Review declarations
		consent_truthfulness: z.boolean().optional(),
		consent_pdpa: z.boolean().optional(),
	};
}

/**
 * Identity fields: locked for the registering organisation once any card of
 * the person has been submitted; the host and admins can still correct them.
 */
export const ACCREDITATION_IDENTITY_FIELDS = [
	"country_code",
	"identification_type",
	"identification_number",
	"written_name_in_identification",
	"first_middle_name",
	"last_surname",
	"has_last_surname",
	"date_of_birth",
] as const;

/** A new person (Personal checkpoint): ID, nationality, given names and date of birth are required. */
export function makeCreatePersonFields(m: M = enAccreditationFormMessages) {
	return z.object({
		...personFields(m),
		country_code: countryField(m),
		identification_type: z.enum(IDENTIFICATION_TYPES),
		identification_number: idNumberField(m),
		first_middle_name: requiredText(m),
		date_of_birth: dateField(m),
	});
}

export function makePatchPersonFields(m: M = enAccreditationFormMessages) {
	return z.object(personFields(m));
}

// ── Card ────────────────────────────────────────────────────────────────────

/** `ac_cards` fields other than the chain. */
function cardFields(m: M) {
	return {
		sport_id: uuid.nullable().optional(),
		accreditation_name: optionalText(m, ACCREDITATION_NAME_MAX, m.accreditationNameMax),
		tv_short_name: optionalText(m, TV_SHORT_NAME_MAX, m.tvShortNameMax),
		delegation_role: optionalText(m),
		urgent_printing: z.boolean().optional(),
		employer_media_organization: optionalText(m),
		original_job: optionalText(m),
		employer_phone_country_code: optionalText(m, 8),
		employer_phone_number: optionalText(m, PHONE_MAX),
		employer_email_address: optionalEmail(m),
		employer_address: optionalText(m, ACCREDITATION_LONG_TEXT_MAX),
	};
}

/** The card's own columns other than the chain, all optional (PATCH's flat card keys) — for bodies that take a subset. */
export function makePatchCardFields(m: M = enAccreditationFormMessages) {
	return z.object(cardFields(m));
}

/**
 * The card's travel (M9), nested in create / PATCH: the whole leg, or `null`
 * to remove it (on create, `null` is the same as leaving it out). The leg's
 * messages come from the travel factory; pass translated ones from the web.
 */
function travelFields(t: TravelFormMessages) {
	return {
		arrival: makeTravelLegFields(t).nullable().optional(),
		departure: makeTravelLegFields(t).nullable().optional(),
	};
}

/** The chain: changes only in DRAFT / RETURN for the organisation (admins: any status). */
export const ACCREDITATION_CHAIN_FIELDS = ["games_id", "responsible_organization_id", "main_function_id", "sub_agency_id", "sub_function_id"] as const;

const reasonField = z.string().trim().min(1).max(ACCREDITATION_REASON_MAX);

/**
 * `POST /accreditations` — the Personal checkpoint: Zone + Personal, creating
 * the DRAFT and its registration number. Either `person` (a new person) or
 * `people_id` (add a card to a person the caller can already see — the
 * "already in your records" offer, or Multicard).
 */
export function makeCreateAccreditationForm(m: M = enAccreditationFormMessages, t: TravelFormMessages = enTravelFormMessages) {
	return z
		.object({
			games_id: uuid,
			responsible_organization_id: uuid,
			main_function_id: uuid,
			sub_agency_id: uuid.nullable().optional(),
			sub_function_id: uuid.nullable().optional(),
			...cardFields(m),
			people_id: uuid.optional(),
			person: makeCreatePersonFields(m).optional(),
			...travelFields(t),
		})
		.refine((v) => (v.people_id === undefined) !== (v.person === undefined), { path: ["person"], message: m.personOrPeopleId })
		.refine(departureNotBeforeArrival, { path: ["departure", "travel_date"], message: t.departureBeforeArrival });
}
export const createAccreditationBody = makeCreateAccreditationForm();
export type CreateAccreditationBody = z.input<typeof createAccreditationBody>;

/**
 * `PATCH /accreditations/:id` — any subset, plus the `version` the client
 * read (409 STALE_VERSION when someone else saved first). `null` clears a
 * nullable field; omitted leaves it alone. `arrival` / `departure` (M9):
 * the whole leg replaces the stored one; `null` removes it.
 */
export function makePatchAccreditationForm(m: M = enAccreditationFormMessages, t: TravelFormMessages = enTravelFormMessages) {
	return z
		.object({
			version: z.number().int().min(1),
			games_id: uuid.optional(),
			responsible_organization_id: uuid.optional(),
			main_function_id: uuid.optional(),
			sub_agency_id: uuid.nullable().optional(),
			sub_function_id: uuid.nullable().optional(),
			...cardFields(m),
			person: makePatchPersonFields(m).optional(),
			...travelFields(t),
			reason: reasonField.optional(),
		})
		.refine(
			// `card` includes `arrival` / `departure`: a PATCH that only changes travel is a change.
			({ version: _v, reason: _r, person, ...card }) =>
				Object.values(card).some((x) => x !== undefined) || (person !== undefined && Object.values(person).some((x) => x !== undefined)),
			{ message: m.nothingToChange },
		)
		.refine(departureNotBeforeArrival, { path: ["departure", "travel_date"], message: t.departureBeforeArrival });
}
export const patchAccreditationBody = makePatchAccreditationForm();
export type PatchAccreditationBody = z.input<typeof patchAccreditationBody>;

// ── Transitions ─────────────────────────────────────────────────────────────

/** Every edge of `action` requires a remark (RETURN, REJECT). */
export const transitionNeedsRemarks = (action: AcCardTransitionAction): boolean => {
	const edges = AC_CARD_TRANSITIONS.filter((t) => t.action === action);
	return edges.length > 0 && edges.every((t) => t.remarks_required === true);
};

/**
 * Every edge of `action` requires a `reason` (the audit headline) — every
 * step but submit / approve (`AC_CARD_REASON_EXEMPT_ACTIONS`) and return /
 * reject, whose remark is their reason. Read by the bodies below and the
 * API's up-front check, so a missing reason is a 422 before any SQL.
 */
export const transitionNeedsReason = (action: AcCardTransitionAction): boolean => {
	const edges = AC_CARD_TRANSITIONS.filter((t) => t.action === action);
	return edges.length > 0 && edges.every((t) => t.reason_required === true);
};

/** The status an action moves a card to — one per action. */
export const transitionTarget = (action: AcCardTransitionAction): AcCardStatus => {
	const edge = AC_CARD_TRANSITIONS.find((t) => t.action === action);
	if (!edge) throw new Error(`no edge for ${action}`);
	return edge.to;
};

/**
 * `POST /accreditations/:id/{action}` body. `remarks` (shown to the
 * organisation, stored in `status_note`) is required for return / reject;
 * `reason` (the audit headline) for every other step but submit / approve
 * (`transitionNeedsReason`, 2026-10-30). Pass `action` to get the form that
 * requires both up front; the server checks both per action.
 */
export function makeTransitionForm(m: M = enAccreditationFormMessages, action?: AcCardTransitionAction) {
	const remarks = z.string().trim().min(1, m.remarksRequired).max(AC_CARD_REMARKS_MAX, m.textTooLong);
	const reason = z.string({ required_error: m.required }).trim().min(1, m.required).max(ACCREDITATION_REASON_MAX, m.textTooLong);
	return z.object({
		remarks: action && transitionNeedsRemarks(action) ? remarks : remarks.optional(),
		reason: action && transitionNeedsReason(action) ? reason : reason.optional(),
	});
}
export const transitionBody = makeTransitionForm();
export type TransitionBody = z.input<typeof transitionBody>;

/**
 * `POST /accreditations/:id/status` — the SUPER_ADMIN override (user decision
 * 2026-10-15): any status to any other, off the graph. `reason` is required:
 * it is the audit headline, and the remark (`status_note`) when the target is
 * RETURN / REJECTED. `version` as in PATCH → STALE_VERSION; the same status →
 * STATUS_UNCHANGED. Answers `AccreditationWriteResult`.
 */
export function makeSetAccreditationStatusForm(m: M = enAccreditationFormMessages) {
	return z.object({
		status: z.enum(AC_CARD_STATUSES),
		reason: z.string().trim().min(1, m.required).max(ACCREDITATION_REASON_MAX, m.textTooLong),
		version: z.number().int().min(1),
	});
}
export const setAccreditationStatusBody = makeSetAccreditationStatusForm();
export type SetAccreditationStatusBody = z.input<typeof setAccreditationStatusBody>;

// ── Bulk ────────────────────────────────────────────────────────────────────

/** The records-list filters (`GET /accreditations` query string, as strings), without paging. */
export const accreditationBulkFilter = accreditationListQuery.omit({ cursor: true, limit: true, sort: true });
export type AccreditationBulkFilter = z.input<typeof accreditationBulkFilter>;

/**
 * Either explicit `ids` (≤ BULK_ACCREDITATIONS_MAX_IDS), or "Select all N":
 * the list's `filter` plus the `expected_count` the user saw. The server
 * re-counts in scope inside the transaction: a different number is 409
 * BULK_FILTER_CHANGED; more than BULK_ACCREDITATIONS_MAX_FILTER is 422.
 */
const bulkTargetFields = {
	ids: z.array(uuid).min(1).max(BULK_ACCREDITATIONS_MAX_IDS).optional(),
	filter: accreditationBulkFilter.optional(),
	expected_count: z.number().int().min(1).max(BULK_ACCREDITATIONS_MAX_FILTER).optional(),
};
const oneTarget = (v: { ids?: unknown; filter?: unknown; expected_count?: unknown }) =>
	v.ids !== undefined ? v.filter === undefined && v.expected_count === undefined : v.filter !== undefined && v.expected_count !== undefined;

export function makeBulkStatusForm(m: M = enAccreditationFormMessages) {
	const remarks = z.string().trim().min(1, m.remarksRequired).max(AC_CARD_REMARKS_MAX, m.textTooLong);
	return z
		.object({ action: z.enum(AC_CARD_TRANSITION_ACTIONS), remarks: remarks.optional(), reason: reasonField.optional(), ...bulkTargetFields })
		.refine(oneTarget, { path: ["ids"], message: m.idsOrFilter })
		.refine((v) => !transitionNeedsRemarks(v.action) || v.remarks !== undefined, { path: ["remarks"], message: m.remarksRequired })
		// One reason for the whole batch (2026-10-30): every step but submit / approve / return / reject.
		.refine((v) => !transitionNeedsReason(v.action) || v.reason !== undefined, { path: ["reason"], message: m.required });
}
export const bulkStatusBody = makeBulkStatusForm();
export type AccreditationBulkStatusBody = z.input<typeof bulkStatusBody>;

export function makeBulkUrgentForm(m: M = enAccreditationFormMessages) {
	return z.object({ urgent_printing: z.boolean(), reason: reasonField.optional(), ...bulkTargetFields }).refine(oneTarget, { path: ["ids"], message: m.idsOrFilter });
}
export const bulkUrgentBody = makeBulkUrgentForm();
export type AccreditationBulkUrgentBody = z.input<typeof bulkUrgentBody>;

/**
 * Why a selected record was left alone by a bulk action.
 *
 *   not_in_scope     not visible to the caller, or does not exist
 *   locked           its status has no such edge (e.g. approve a DRAFT)
 *   permission       visible, but the caller may not take this action on it
 *   window           registration is closed for its category (first submit)
 *   self_approval    the caller submitted it
 *   incomplete       submit: required fields are missing
 *   reason_required  this edge needs a `reason` and none was given
 */
export const BULK_REFUSAL_REASONS = ["not_in_scope", "locked", "permission", "window", "self_approval", "incomplete", "reason_required"] as const;
export type BulkRefusalReason = (typeof BULK_REFUSAL_REASONS)[number];

export interface AccreditationBulkResult {
	updated: number;
	refused: Array<{ ac_card_id: string; reason: BulkRefusalReason }>;
}

// ── Card access ─────────────────────────────────────────────────────────────

/** `PUT /accreditations/:id/access` — replace-all; at most one TRANSPORT, active codes only (checked by the server). */
export const setCardAccessBody = z.object({
	access_code_ids: z
		.array(uuid)
		.max(ACCESS_DEFAULTS_MAX)
		.refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, "Each access code may appear once"),
	reason: reasonField.optional(),
});
export type SetCardAccessBody = z.input<typeof setCardAccessBody>;

// ── Delete ──────────────────────────────────────────────────────────────────

/** `DELETE /accreditations/:id` — SUPER_ADMIN, DRAFT only, confirmed with the caller's own password. */
export const deleteAccreditationBody = z.object({
	confirm_password: z.string().min(1, "Enter your own password to confirm").max(200),
});
export type DeleteAccreditationBody = z.input<typeof deleteAccreditationBody>;

// ── Write responses ─────────────────────────────────────────────────────────

/**
 * What every single-card write answers: enough to update the page and its
 * `version`; the full record is `GET /accreditations/:id`.
 */
export interface AccreditationWriteResult {
	ac_card_id: string;
	people_id: string;
	status: AcCardStatus;
	version: number;
	registration_no: number;
	registration_no_display: string;
	accreditation_no: number | null;
	accreditation_no_display: string | null;
	status_note: string | null;
	updated_at: string;
}

/** `DELETE /accreditations/:id`. `person_deleted`: the person had nothing else and went too. */
export interface AccreditationDeleteResult {
	ac_card_id: string;
	person_deleted: boolean;
}

// ── Registration windows ────────────────────────────────────────────────────

export function makeRegistrationWindowForm(m: M = enAccreditationFormMessages) {
	return z
		.object({
			games_id: uuid,
			main_function_id: uuid,
			/** null = every organisation. */
			responsible_organization_id: uuid.nullable(),
			opens_at: z.string().trim().datetime({ offset: true, message: m.dateTimeInvalid }),
			closes_at: z.string().trim().datetime({ offset: true, message: m.dateTimeInvalid }),
		})
		.refine((v) => Date.parse(v.closes_at) > Date.parse(v.opens_at), { path: ["closes_at"], message: m.closesAfterOpens });
}
export const createRegistrationWindowBody = makeRegistrationWindowForm();
export type CreateRegistrationWindowBody = z.input<typeof createRegistrationWindowBody>;

/** PATCH: any subset; the server re-checks closes > opens against the stored row (CHECK). */
export function makePatchRegistrationWindowForm(m: M = enAccreditationFormMessages) {
	return z
		.object({
			main_function_id: uuid.optional(),
			responsible_organization_id: uuid.nullable().optional(),
			opens_at: z.string().trim().datetime({ offset: true, message: m.dateTimeInvalid }).optional(),
			closes_at: z.string().trim().datetime({ offset: true, message: m.dateTimeInvalid }).optional(),
			reason: reasonField.optional(),
		})
		.refine((v) => v.opens_at === undefined || v.closes_at === undefined || Date.parse(v.closes_at) > Date.parse(v.opens_at), {
			path: ["closes_at"],
			message: m.closesAfterOpens,
		})
		.refine(({ reason: _r, ...rest }) => Object.values(rest).some((x) => x !== undefined), { message: m.nothingToChange });
}
export const patchRegistrationWindowBody = makePatchRegistrationWindowForm();
export type PatchRegistrationWindowBody = z.input<typeof patchRegistrationWindowBody>;

/** `GET /registration-windows` — admin list, offset paging (a small table). */
export const listRegistrationWindowsQuery = paginationQuery.extend({
	games_id: uuid.optional(),
	main_function_id: uuid.optional(),
	responsible_organization_id: uuid.optional(),
});
export type ListRegistrationWindowsQuery = z.infer<typeof listRegistrationWindowsQuery>;

/** `GET /registration-windows/open?games_id=` — any GMS role. */
export const openRegistrationWindowsQuery = z.object({ games_id: uuid });
export type OpenRegistrationWindowsQuery = z.infer<typeof openRegistrationWindowsQuery>;

/** At most this many rows from `/open` — a guard, not a page. */
export const OPEN_WINDOWS_LIMIT = 500;

export interface RegistrationWindowItem {
	registration_window_id: string;
	games: { id: string; code: string };
	main_function: { id: string; code: string; name: string };
	/** null = every organisation. */
	responsible_organization: { id: string; name: string } | null;
	opens_at: string;
	closes_at: string;
	/** `now()` is inside [opens_at, closes_at], by the database clock. */
	is_open: boolean;
	created_at: string;
	updated_at: string;
}

/**
 * One row of `/open`: a window that applies to the caller, open now or not.
 * Since 2026-10-22 a (category, organisation) with NO matching window is open
 * to a scoped login; with windows, open only while one is (`registrationOpenFor`).
 */
export interface OpenRegistrationWindow {
	registration_window_id: string;
	games_id: string;
	main_function_id: string;
	responsible_organization_id: string | null;
	opens_at: string;
	closes_at: string;
	/** Open right now. */
	is_open: boolean;
}

/**
 * The register page's mirror of the API's `windowOpenSql` (the server decides
 * again on create and first submit): no window for the (category,
 * organisation) → open; windows → open while one of them is.
 */
export function registrationOpenFor(windows: readonly OpenRegistrationWindow[], mainFunctionId: string, organizationId: string): boolean {
	const matching = windows.filter(
		(w) => w.main_function_id === mainFunctionId && (w.responsible_organization_id === null || w.responsible_organization_id === organizationId),
	);
	return matching.length === 0 || matching.some((w) => w.is_open);
}
